import React from "react";
import { Document, Page, Text, View, StyleSheet, Link } from "@react-pdf/renderer";

const ACCENT = "#0e1218";
const MUTED = "#666a75";
const LINE = "#e6e6e8";

const styles = StyleSheet.create({
  page: { backgroundColor: "#fdfdfd", color: "#0e1218", fontFamily: "Helvetica", fontSize: 9.5, flexDirection: "row" },
  sidebar: { width: 180, backgroundColor: "#0e1218", color: "#ffffff", padding: 28, paddingTop: 40 },
  main: { flex: 1, padding: 36, paddingTop: 40 },
  name: { fontSize: 22, fontFamily: "Helvetica-Bold", color: "#ffffff", lineHeight: 1.05 },
  role: { fontSize: 9, color: "#c0c2c8", marginTop: 6, lineHeight: 1.4 },
  sbSection: { marginTop: 22 },
  sbTitle: { fontSize: 7.5, fontFamily: "Helvetica-Bold", color: "#e6c769", letterSpacing: 1.8, marginBottom: 8 },
  sbItem: { fontSize: 9, color: "#e8eaef", marginBottom: 4 },
  sbLink: { fontSize: 8.5, color: "#e6c769", marginBottom: 3, textDecoration: "none" },
  divider: { height: 0.5, backgroundColor: LINE, marginTop: 12, marginBottom: 8 },
  mainSectionTitle: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    color: ACCENT,
    letterSpacing: 2,
    marginTop: 10,
    marginBottom: 8,
  },
  entry: { marginBottom: 10 },
  entryHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" },
  entryTitle: { fontSize: 10.5, fontFamily: "Helvetica-Bold", color: ACCENT },
  entrySpan: { fontSize: 8.5, color: MUTED, fontFamily: "Helvetica" },
  entrySub: { fontSize: 9, color: MUTED, marginTop: 1.5 },
  entryBody: { fontSize: 9, color: "#2a2d36", marginTop: 3, lineHeight: 1.5 },
  bullet: { flexDirection: "row", marginTop: 1.5 },
  bulletMark: { width: 9, fontSize: 9, color: "#e6c769" },
  bulletText: { flex: 1, fontSize: 9, color: "#2a2d36" },
  summary: { fontSize: 10, color: "#2a2d36", marginBottom: 12, lineHeight: 1.55, fontFamily: "Helvetica-Oblique" },
  skillGroup: { marginTop: 6 },
  skillGroupTitle: { fontSize: 7, color: "#e6c769", letterSpacing: 1.5, marginBottom: 2 },
  skillLine: { fontSize: 8.5, color: "#e8eaef", lineHeight: 1.5 },
});

const groupSkills = (skills) => {
  const groups = {};
  skills.forEach((s) => {
    const g = s.group || "Other";
    if (!groups[g]) groups[g] = [];
    groups[g].push(s.name);
  });
  return groups;
};

const MinimalModern = ({ profile, credentials, awards, experiences, projects, skills }) => {
  const grouped = groupSkills(skills);
  const groupOrder = ["Build", "Capital", "Analyse", "Frontier", "People"];
  const orderedGroups = [
    ...groupOrder.filter((g) => grouped[g]),
    ...Object.keys(grouped).filter((g) => !groupOrder.includes(g)),
  ];

  return (
    <Document
      author={profile.name}
      title={`${profile.name} — Resume`}
      subject="Curriculum Vitae"
    >
      <Page size="A4" style={styles.page}>
        {/* SIDEBAR */}
        <View style={styles.sidebar}>
          <Text style={styles.name}>{profile.name}</Text>
          <Text style={styles.role}>
            {profile.titles?.join(" · ") || "Entrepreneur · Investor"}
          </Text>

          <View style={styles.sbSection}>
            <Text style={styles.sbTitle}>CONTACT</Text>
            {profile.email && <Text style={styles.sbItem}>{profile.email}</Text>}
            {profile.phone && <Text style={styles.sbItem}>{profile.phone}</Text>}
            {profile.location && <Text style={styles.sbItem}>{profile.location}</Text>}
          </View>

          <View style={styles.sbSection}>
            <Text style={styles.sbTitle}>LINKS</Text>
            <Link src="https://utkarshbokadia.com" style={styles.sbLink}>utkarshbokadia.com</Link>
            {profile.linkedin && <Link src={profile.linkedin} style={styles.sbLink}>LinkedIn</Link>}
          </View>

          {orderedGroups.length > 0 && (
            <View style={styles.sbSection}>
              <Text style={styles.sbTitle}>SKILLS</Text>
              {orderedGroups.map((g) => (
                <View key={g} style={styles.skillGroup}>
                  <Text style={styles.skillGroupTitle}>{String(g).toUpperCase()}</Text>
                  <Text style={styles.skillLine}>{grouped[g].join(" · ")}</Text>
                </View>
              ))}
            </View>
          )}

          {awards.length > 0 && (
            <View style={styles.sbSection}>
              <Text style={styles.sbTitle}>Awards</Text>
              {awards.slice(0, 5).map((a) => (
                <Text key={a.id} style={styles.sbItem}>
                  {a.title}{a.year ? ` · ${a.year}` : ""}
                </Text>
              ))}
            </View>
          )}
        </View>

        {/* MAIN */}
        <View style={styles.main}>
          {profile.summary && <Text style={styles.summary}>{profile.summary}</Text>}

          <Text style={styles.mainSectionTitle}>EXPERIENCE</Text>
          {experiences.slice(0, 6).map((e) => (
            <View key={e.id} style={styles.entry} wrap={false}>
              <View style={styles.entryHead}>
                <Text style={styles.entryTitle}>
                  {e.company}
                  {e.current ? "  ● Current" : ""}
                </Text>
                <Text style={styles.entrySpan}>{e.span}</Text>
              </View>
              <Text style={styles.entrySub}>{e.role} · {e.location}</Text>
              {e.summary && <Text style={styles.entryBody}>{e.summary}</Text>}
              {Array.isArray(e.achievements) && e.achievements.slice(0, 3).map((a, i) => (
                <View key={i} style={styles.bullet}>
                  <Text style={styles.bulletMark}>›</Text>
                  <Text style={styles.bulletText}>{a}</Text>
                </View>
              ))}
            </View>
          ))}

          {projects.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.mainSectionTitle}>SELECTED WORK</Text>
              {projects.slice(0, 3).map((p) => (
                <View key={p.id} style={styles.entry} wrap={false}>
                  <View style={styles.entryHead}>
                    <Text style={styles.entryTitle}>{p.name}</Text>
                    <Text style={styles.entrySpan}>{p.year}</Text>
                  </View>
                  <Text style={styles.entrySub}>{p.tagline}{p.displayUrl ? ` · ${p.displayUrl}` : ""}</Text>
                </View>
              ))}
            </>
          )}

          <View style={styles.divider} />
          <Text style={styles.mainSectionTitle}>CREDENTIALS</Text>
          {credentials.slice(0, 10).map((c) => (
            <View key={c.id} style={styles.entry} wrap={false}>
              <View style={styles.entryHead}>
                <Text style={styles.entryTitle}>{c.title}</Text>
                <Text style={styles.entrySpan}>{c.issued}</Text>
              </View>
              <Text style={styles.entrySub}>{c.issuer}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default MinimalModern;
